/**
 * CommandPalette — keyboard-first universal navigator.
 *
 *   • Open with ⌘K / Ctrl+K or "/" (when no input is focused).
 *   • Type to fuzzy-search every tool, page, or game.
 *   • Recents + Pinned appear when the query is empty.
 *   • Arrow keys to move, Enter to navigate, ⌘P / Ctrl+P to pin.
 *
 * Visual contract: glass surface, gradient inner ring, category-tinted
 * icon chips, soft staggered fade-in.  Everything is mounted via a portal
 * so it floats above any sticky / z-index nightmare elsewhere.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass, X, ArrowElbowDownLeft, PushPin, PushPinSimpleSlash,
  Clock, Lightning, Command, ArrowUp, ArrowDown,
} from '@phosphor-icons/react';

const META = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent) ? '⌘' : 'Ctrl';

/* fuzzy score — higher = better match.  Returns 0 when no match. */
function score(item, q) {
  if (!q) return 1; // unsorted; caller decides order
  const t = q.toLowerCase();
  const label = (item.label || '').toLowerCase();
  const desc = (item.desc || '').toLowerCase();
  const cat = (item.category || '').toLowerCase();
  if (label === t) return 1000;
  if (label.startsWith(t)) return 500;
  if (label.includes(t)) return 250;
  // contiguous match in desc / category
  if (desc.includes(t)) return 80;
  if (cat.includes(t)) return 40;
  // Subsequence (letters in order) — slow last-resort
  let i = 0, j = 0; const s = label;
  while (i < s.length && j < t.length) { if (s[i] === t[j]) j++; i++; }
  return j === t.length ? 20 : 0;
}

const SectionLabel = ({ children, count, icon: Icon }) => (
  <div className="flex items-center gap-2 px-3 pt-3 pb-1.5 text-[10px] font-black uppercase tracking-[0.18em]"
    style={{ color: 'var(--cs-text-dim)', fontFamily: 'Outfit, sans-serif' }}>
    {Icon && <Icon size={11} weight="bold" />}
    <span>{children}</span>
    {typeof count === 'number' && (
      <span className="font-mono text-[9px] opacity-60">{count}</span>
    )}
    <div className="h-px flex-1 ml-1" style={{ background: 'linear-gradient(90deg, var(--cs-border), transparent)' }} />
  </div>
);

const KeyChip = ({ children, accent }) => (
  <kbd className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded text-[10px] font-bold border"
    style={{
      background: accent ? 'linear-gradient(135deg, rgba(205,4,11,0.20), rgba(205,4,11,0.06))' : 'var(--cs-bg-surface-2)',
      borderColor: accent ? 'rgba(205,4,11,0.40)' : 'var(--cs-border)',
      color: accent ? '#FF4B51' : 'var(--cs-text-muted)',
      fontFamily: 'JetBrains Mono, monospace',
    }}>
    {children}
  </kbd>
);

/* Single result row */
const Row = ({ item, active, pinned, onSelect, onTogglePin, refCb }) => {
  const Icon = item.icon;
  const accent = item.color || '#A3A3A3';
  return (
    <div
      ref={refCb}
      onMouseDown={(e) => { e.preventDefault(); onSelect(item); }}
      className={`group flex items-center gap-3 mx-1.5 px-2.5 py-2 rounded-lg cursor-pointer transition-all duration-150 ${active ? 'cmd-row-active' : ''}`}
      style={active ? {
        background: `linear-gradient(135deg, ${accent}18, ${accent}05)`,
        boxShadow: `inset 0 0 0 1px ${accent}30, 0 6px 18px ${accent}20`,
      } : {}}
      data-testid={`cmd-row-${item.path}`}>
      {/* Icon chip */}
      <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105"
        style={{
          background: `linear-gradient(135deg, ${accent}25, ${accent}08)`,
          border: `1px solid ${accent}35`,
          color: accent,
          boxShadow: active ? `0 4px 12px ${accent}30` : 'none',
        }}>
        {Icon ? <Icon size={15} weight={active ? 'fill' : 'regular'} /> : <Lightning size={15} />}
      </div>
      {/* Label + desc */}
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-tight truncate"
          style={{ color: 'var(--cs-text)', fontFamily: 'Outfit, sans-serif' }}>
          {item.label}
          {item.external && <span className="ml-1 text-[9px] align-top opacity-50">↗</span>}
        </div>
        <div className="text-[10px] truncate" style={{ color: 'var(--cs-text-dim)' }}>
          <span className="font-mono opacity-70 mr-1.5">{item.category}</span>
          {item.desc}
        </div>
      </div>
      {/* Pin toggle */}
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(item); }}
        className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition-all ${pinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-70 hover:opacity-100'}`}
        style={{
          color: pinned ? '#F59E0B' : 'var(--cs-text-muted)',
          background: pinned ? 'rgba(245,158,11,0.10)' : 'transparent',
        }}
        title={pinned ? 'Unpin' : 'Pin'}
        aria-label={pinned ? 'Unpin' : 'Pin'}
        data-testid={`cmd-pin-${item.path}`}>
        {pinned ? <PushPin size={12} weight="fill" /> : <PushPinSimpleSlash size={12} weight="bold" />}
      </button>
      {/* Active indicator */}
      <div className="shrink-0 hidden sm:flex items-center" style={{ minWidth: 20 }}>
        {active && (
          <ArrowElbowDownLeft size={13} weight="bold" style={{ color: accent }} />
        )}
      </div>
    </div>
  );
};

const CommandPalette = ({ open, onClose, tools, recents, pins, isPinned, togglePin }) => {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const rowRefs = useRef([]);
  const navigate = useNavigate();

  // tool lookup by path
  const byPath = useMemo(() => {
    const m = new Map();
    (tools || []).forEach(t => m.set(t.path, t));
    return m;
  }, [tools]);

  // Build the visible sections
  const sections = useMemo(() => {
    const q = query.trim();
    if (!q) {
      const pinned = pins.map(p => byPath.get(p)).filter(Boolean);
      const rec = recents.map(p => byPath.get(p)).filter(Boolean)
        .filter(it => !pins.includes(it.path)); // de-dupe
      return [
        ...(pinned.length ? [{ key: 'pinned', label: 'Pinned', icon: PushPin, items: pinned }] : []),
        ...(rec.length ? [{ key: 'recent', label: 'Recent', icon: Clock, items: rec }] : []),
        { key: 'all', label: 'All Tools', icon: Lightning, items: tools },
      ];
    }
    // Search mode — score & sort, flatten into a single section
    const scored = (tools || [])
      .map(it => ({ it, s: score(it, q) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map(x => x.it);
    return [{ key: 'results', label: 'Results', icon: MagnifyingGlass, items: scored }];
  }, [query, tools, recents, pins, byPath]);

  // Flat ordered list — for keyboard nav
  const flat = useMemo(() => sections.flatMap(s => s.items), [sections]);

  // Reset on open / on query change
  useEffect(() => { setActiveIdx(0); }, [query, open]);
  useEffect(() => {
    if (open) {
      setQuery('');
      // Tiny delay so focus survives the framer enter animation
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Keep active row in view
  useEffect(() => {
    const el = rowRefs.current[activeIdx];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  const handleSelect = (item) => {
    if (!item) return;
    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, Math.max(flat.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(flat[activeIdx]);
      return;
    }
    // ⌘P / Ctrl+P → pin currently active row
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'p') {
      e.preventDefault();
      const it = flat[activeIdx];
      if (it) togglePin(it.path);
      return;
    }
  };

  if (!open) return null;

  // Index map: section.items[i] → flat index
  let cursor = -1;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120] flex items-start justify-center pt-[12vh] px-3 sm:px-4"
          style={{ background: 'rgba(5,5,5,0.74)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
          data-testid="command-palette">
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="cmd-shell relative w-full max-w-[640px] rounded-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: 'min(70vh, 640px)' }}>

            {/* Soft ambient glow */}
            <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(205,4,11,0.18), transparent 65%)' }} />
            <div aria-hidden className="pointer-events-none absolute -bottom-24 -right-16 w-72 h-72 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.14), transparent 65%)' }} />

            {/* Search bar */}
            <div className="relative shrink-0 border-b flex items-center gap-2 px-3.5 py-3"
              style={{ borderColor: 'var(--cs-border)', background: 'var(--cs-bg-surface)' }}>
              <MagnifyingGlass size={16} weight="bold" style={{ color: 'var(--cs-text-muted)' }} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Jump to anywhere…"
                className="flex-1 bg-transparent border-0 outline-none text-[14px] font-medium placeholder:text-[var(--cs-text-dim)]"
                style={{ color: 'var(--cs-text)', fontFamily: 'Outfit, sans-serif' }}
                data-testid="command-palette-input" />
              {query && (
                <button onMouseDown={(e) => { e.preventDefault(); setQuery(''); inputRef.current?.focus(); }}
                  className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-[var(--cs-bg-hover)]"
                  style={{ color: 'var(--cs-text-muted)' }}
                  data-testid="command-palette-clear">
                  <X size={12} weight="bold" />
                </button>
              )}
              <KeyChip>ESC</KeyChip>
            </div>

            {/* Result list */}
            <div ref={listRef} className="overflow-y-auto cs-hide-scrollbar pb-2 flex-1">
              {flat.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-2"
                    style={{ background: 'var(--cs-bg-surface)', color: 'var(--cs-text-muted)' }}>
                    <MagnifyingGlass size={20} />
                  </div>
                  <div className="text-[13px] font-bold" style={{ color: 'var(--cs-text)' }}>
                    No matches for "{query}"
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: 'var(--cs-text-dim)' }}>
                    Try a different keyword or part of a tool name.
                  </div>
                </div>
              ) : (
                sections.map(sec => {
                  if (!sec.items.length) return null;
                  return (
                    <div key={sec.key}>
                      <SectionLabel icon={sec.icon} count={sec.items.length}>{sec.label}</SectionLabel>
                      <div className="space-y-0.5">
                        {sec.items.map(item => {
                          cursor += 1;
                          const isActive = cursor === activeIdx;
                          const localCursor = cursor;
                          return (
                            <Row
                              key={`${sec.key}-${item.path}`}
                              item={item}
                              active={isActive}
                              pinned={isPinned(item.path)}
                              onSelect={handleSelect}
                              onTogglePin={(it) => togglePin(it.path)}
                              refCb={(el) => { rowRefs.current[localCursor] = el; }} />
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer hints */}
            <div className="shrink-0 border-t px-3 py-2 flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider flex-wrap"
              style={{ borderColor: 'var(--cs-border)', background: 'var(--cs-bg-surface)', color: 'var(--cs-text-muted)' }}>
              <span className="inline-flex items-center gap-1"><KeyChip><ArrowUp size={9} weight="bold" /></KeyChip><KeyChip><ArrowDown size={9} weight="bold" /></KeyChip> Navigate</span>
              <span className="inline-flex items-center gap-1"><KeyChip>↵</KeyChip> Open</span>
              <span className="inline-flex items-center gap-1 hidden sm:inline-flex"><KeyChip>{META}</KeyChip><KeyChip>P</KeyChip> Pin</span>
              <span className="ml-auto inline-flex items-center gap-1 opacity-70">
                <Command size={11} weight="bold" /> Powered by ⌘K
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default CommandPalette;
